/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer, TICKET_CHANGE_EVENTS, TicketLayout } from '../tickets/TicketContainer';
import { Texture, utils } from 'pixi.js-legacy';
import { TicketMeshComponent } from './TicketMeshComponent';
import { Ticket } from '@touchplan/common';
import { convertHexStringToNumber } from 'utils/color-utils';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { cssVar } from '@touchplan/css-var';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';

const TICKET_SELECTED_PADDING = 30;
const TICKET_SELECTED_OFFSET = -15;

export class TicketSelectedTask extends TicketMeshComponent implements ComponentInterface {
	public override renderable = false;

	constructor(
		texture: Texture,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		leftWidth: number = 25,
		topHeight: number = 25,
		rightWidth: number = 25,
		bottomHeight: number = 25
	) {
		super(texture, canvasAngularLand, ticketContainer, leftWidth, topHeight, rightWidth, bottomHeight);

		this.alpha = 1.0;

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);

		this.onChanged(ticketContainer);
	}

	private applyLayoutChanges(data: Ticket, layout: TicketLayout) {
		const width = setTicketWidthHelper(
			data,
			layout,
			this.canvasAngularLand.wideTicketSize$.getValue() as number
		);

		const newHeight = layout.height + TICKET_SELECTED_PADDING;
		const newWidth = width + TICKET_SELECTED_PADDING;

		this.x = TICKET_SELECTED_OFFSET;
		this.y = TICKET_SELECTED_OFFSET;

		if (this.isDirty(newWidth, newHeight)) {
			this.width = newWidth;
			this.height = newHeight;
		}
	}

	public update(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const data: Ticket = ticketContainer.getData();
		const layout: TicketLayout = ticketContainer.getLayout();
		this.renderable =
			data['selected'] || data['cycleSelected'] || (data.view.hasPredecessorOutOfOrder as boolean);
		if (this.visible && this.renderable) {
			this.applyLayoutChanges(data, layout);
		}
	}

	private isDirty(newWidth: number, newHeight: number): boolean {
		return this.height !== newHeight || this.width !== newWidth;
	}

	private onChanged = (ticketContainer: TicketContainer) => {
		const data: Ticket = ticketContainer.getData();
		const layout = ticketContainer.getLayout();

		this.renderable =
			data['selected'] ||
			data['cycleSelected'] ||
			data['isGhost'] ||
			(data.view.hasPredecessorOutOfOrder as boolean);

		if (this.renderable) {
			this.applyLayoutChanges(data, layout);
			// Set the correct texture.
			// The reason why we do this using textures instead of tint is because of https://github.com/pixijs/pixi.js/issues/3862
			if (data['selected']) {
				if (data['cycleSelected']) {
					this.texture = utils.TextureCache['ticketOutlineCycleSelectedMesh'];
					ticketContainer.cutInLine();
				} else {
					this.texture = (<any>Texture).TASK_OUTLINE_SELECTED;
				}
			} else if (data['isGhost']) {
				const color =
					data.view.liveRole && data.view.liveRole.view.color ? data.view.liveRole.view.color : cssVar.black;
				this.tint = convertHexStringToNumber(color);
			} else if (data.view.hasPredecessorOutOfOrder) {
				this.texture = (<any>Texture).TASK_OUTLINE_ERROR;
			}
		}
	};

	public override destroy(options?) {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		super.destroy(options);
	}
}
